import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as dropboxService from '../../../../services/dropbox';
import { dropboxBaseURL } from '../../../../constants/ApiEndPoints';

/**
 * PreviewModal Component
 * 
 * Handles file preview for various file types with special handling for S3-hosted files.
 * 
 * S3 File Preview Strategy:
 * 1. Primary: Use backend streaming endpoint (/files/{id}/stream) that serves files with Content-Disposition: inline
 * 2. Fallback: Individual component error handlers try alternative URLs
 * 3. Last Resort: Download option when preview fails
 * 
 * Supported File Types:
 * - Images: jpg, jpeg, png, gif, bmp, webp, svg, ico, tiff, tif
 * - PDFs: pdf
 * - Text: txt, md, json, xml, csv, log, js, ts, jsx, tsx, css, html, htm, yaml, yml
 * - Video: mp4, webm, ogg, mov, avi, mkv, wmv, flv, m4v
 * - Audio: mp3, wav, ogg, m4a, aac, flac, wma
 * - Office: doc, docx, xls, xlsx, ppt, pptx (download-only)
 */
interface PreviewModalProps {
  file: any;
  onClose: () => void;
  onPreviewTracked?: () => void; // Callback when preview is tracked
}

const PreviewModal: React.FC<PreviewModalProps> = ({ file, onClose, onPreviewTracked }) => {
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  // Role-based permissions
  const canAccess = useSelector((state: any) => state.auth.permissions?.opsmanual);

  // Helper function to track preview and call callback
  const trackPreview = () => {
    // dropboxService.trackFileAccess(file.id, 'previewed');
    // if (onPreviewTracked) {
    //   onPreviewTracked();
    // }
  };

  useEffect(() => {
    loadPreview();
    
    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [file, blobUrl]);

  const loadPreview = async () => {
    try {
      setLoading(true);
      setError(null);

      // console.log('Loading preview for file:', file.id, file.name);

      // For S3-based files, we need to use a proxy approach to avoid Content-Disposition issues
      // Try preview endpoint first
      let response = await dropboxService.previewFile(file.id);
      
      // console.log('Preview response:', response);
      
      if (response?.previewUrl || (response?.success && (response?.data?.downloadUrl || response?.data?.previewUrl))) {
        // Handle direct API response format: {previewUrl: "...", fileName: "...", etc}
        const url = response.previewUrl || response.data?.previewUrl || response.data?.downloadUrl;
        // console.log('Original URL:', url);
        
        // If it's an S3 URL, check if it already has inline content-disposition
        if (isS3Url(url)) {
          // For PDFs and other previewable files, use the S3 URL directly if it has inline content-disposition
          if (url.includes('response-content-disposition=inline') || url.includes('response-content-type=')) {
            setPreviewUrl(url);
          } else {
            // Try using backend proxy URL as fallback
            const proxyUrl = `${dropboxBaseURL}/files/${file.id}/stream`;
            // console.log('Using proxy URL:', proxyUrl);
            setPreviewUrl(proxyUrl);
          }
        } else {
          setPreviewUrl(url);
        }
      } else {
        // Fallback to streaming endpoint
        const streamUrl = `${dropboxBaseURL}/files/${file.id}/stream`;
        // console.log('Using stream URL:', streamUrl);
        setPreviewUrl(streamUrl);
      }
    } catch (err) {
      // console.error('Preview error:', err);
      // Last resort: try streaming endpoint
      try {
        const streamUrl = `${dropboxBaseURL}/files/${file.id}/stream`;
        // console.log('Using fallback stream URL:', streamUrl);
        setPreviewUrl(streamUrl);
      } catch (finalErr) {
        // console.error('Final preview error:', finalErr);
        setError('Failed to load preview');
        toast.error('Failed to load preview');
      }
    } finally {
      setLoading(false);
    }
  };

  const isImage = () => {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tiff', 'tif'];
    return file.extension && imageExtensions.includes(file.extension.toLowerCase());
  };

  const isPDF = () => {
    return file.extension && file.extension.toLowerCase() === 'pdf';
  };

  const isText = () => {
    const textExtensions = ['txt', 'md', 'json', 'xml', 'csv', 'log', 'js', 'ts', 'jsx', 'tsx', 'css', 'html', 'htm', 'yaml', 'yml'];
    return file.extension && textExtensions.includes(file.extension.toLowerCase());
  };

  const isVideo = () => {
    const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'm4v'];
    return file.extension && videoExtensions.includes(file.extension.toLowerCase());
  };

  const isAudio = () => {
    const audioExtensions = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'wma'];
    return file.extension && audioExtensions.includes(file.extension.toLowerCase());
  };

  const isOfficeDocument = () => {
    const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
    return file.extension && officeExtensions.includes(file.extension.toLowerCase());
  };

  // Helper function to check if URL is from S3
  const isS3Url = (url: string) => {
    return url && (url.includes('s3.') || url.includes('amazonaws.com') || url.includes('s3-'));
  };

  // Helper function to create a blob URL for S3 files to bypass Content-Disposition
  const createBlobUrl = async (s3Url: string) => {
    try {
      const response = await fetch(s3Url, {
        method: 'GET',
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch file');
      }
      
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      // console.error('Failed to create blob URL:', error);
      throw error;
    }
  };

  const canPreview = () => {
    return isImage() || isPDF() || isText() || isVideo() || isAudio();
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading preview...</p>
        </div>
      );
    }

    if (error || !previewUrl) {
      return (
        <div className="text-center py-5">
          <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
          <p>{error || 'Preview not available'}</p>
          <div className="mt-3">
            <Button 
              variant="outline-primary" 
              className="mr-2"
              onClick={loadPreview}
              disabled={loading}
            >
              <i className="fas fa-redo mr-2"></i>
              Retry Preview
            </Button>
            <Button 
              variant="primary" 
              onClick={async () => {
                if (!canAccess?.download) {
                  toast.error('You do not have permission to download files');
                  return;
                }
                try {
                  const response = await dropboxService.downloadFile(file.id);
                  if (response?.success && response?.data?.downloadUrl) {
                    window.open(response.data.downloadUrl, '_blank');
                  } else {
                    toast.error('Failed to download file');
                  }
                } catch (err) {
                  toast.error('Failed to download file');
                }
              }}
              disabled={!canAccess?.download}
            >
              <i className="fas fa-download mr-2"></i>
              Download Instead
            </Button>
          </div>
        </div>
      );
    }

    if (!canPreview()) {
      return (
        <div className="text-center py-5">
          <i className="fas fa-file fa-3x text-muted mb-3"></i>
          <p>Preview not available for this file type</p>
          <p className="text-muted">File type: .{file.extension}</p>
          {isOfficeDocument() && (
            <div className="mt-3">
              <p className="text-info">
                <i className="fas fa-info-circle mr-2"></i>
                Office documents require download to view
              </p>
              <Button 
                variant="primary" 
                onClick={async () => {
                  if (!canAccess?.download) {
                    toast.error('You do not have permission to download files');
                    return;
                  }
                  try {
                    const response = await dropboxService.downloadFile(file.id);
                    if (response?.success && response?.data?.downloadUrl) {
                      window.open(response.data.downloadUrl, '_blank');
                    } else {
                      toast.error('Failed to download file');
                    }
                  } catch (err) {
                    toast.error('Failed to download file');
                  }
                }}
                disabled={!canAccess?.download}
              >
                <i className="fas fa-download mr-2"></i>
                Download File
              </Button>
            </div>
          )}
        </div>
      );
    }

    if (isImage()) {
      return (
        <div className="text-center">
          <img 
            src={previewUrl} 
            alt={file.name} 
            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            onError={(e) => {
              // If direct image loading fails (likely due to CORS or Content-Disposition), 
              // try using the streaming endpoint
              const img = e.target as HTMLImageElement;
              if (!img.src.includes('/stream')) {
                const streamUrl = `${dropboxBaseURL}/files/${file.id}/stream`;
                img.src = streamUrl;
              } else {
                setError('Failed to load image');
              }
            }}
            onLoad={() => {
              // Track successful preview
              trackPreview();
            }}
          />
        </div>
      );
    }

    if (isPDF()) {
      return (
        <div style={{ width: '100%', height: '70vh' }}>
          <iframe
            src={previewUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title={file.name}
            onLoad={() => {
              // Track successful preview
              trackPreview();
            }}
            onError={() => {
              // If iframe loading fails, try the streaming endpoint
              if (!previewUrl.includes('/stream')) {
                const streamUrl = `${dropboxBaseURL}/files/${file.id}/stream`;
                setPreviewUrl(streamUrl);
              } else {
                // If streaming also fails, try opening in new tab
                window.open(previewUrl, '_blank');
                setError('PDF opened in new tab');
              }
            }}
          />
        </div>
      );
    }

    if (isVideo()) {
      return (
        <video 
          controls 
          style={{ width: '100%', maxHeight: '70vh' }}
          src={previewUrl}
          onLoadedData={() => {
            // Track successful preview
            trackPreview();
          }}
          onError={(e) => {
            // If video loading fails, try the streaming endpoint
            const video = e.target as HTMLVideoElement;
            if (!video.src.includes('/stream')) {
              const streamUrl = `${dropboxBaseURL}/files/${file.id}/stream`;
              video.src = streamUrl;
            } else {
              setError('Failed to load video');
            }
          }}
        >
          Your browser does not support the video tag.
        </video>
      );
    }

    if (isAudio()) {
      return (
        <div className="text-center py-5">
          <i className="fas fa-music fa-3x text-primary mb-3"></i>
          <p className="mb-3">{file.name}</p>
          <audio 
            controls 
            style={{ width: '100%' }} 
            src={previewUrl}
            onLoadedData={() => {
              // Track successful preview
              trackPreview();
            }}
            onError={(e) => {
              // If audio loading fails, try the streaming endpoint
              const audio = e.target as HTMLAudioElement;
              if (!audio.src.includes('/stream')) {
                const streamUrl = `${dropboxBaseURL}/files/${file.id}/stream`;
                audio.src = streamUrl;
              } else {
                setError('Failed to load audio');
              }
            }}
          >
            Your browser does not support the audio tag.
          </audio>
        </div>
      );
    }

    if (isText()) {
      return (
        <div style={{ width: '100%', height: '70vh' }}>
          <iframe
            src={previewUrl}
            style={{ width: '100%', height: '100%', border: '1px solid #ddd', borderRadius: '4px' }}
            title={file.name}
            onLoad={() => {
              // Track successful preview
              trackPreview();
            }}
            onError={() => {
              // If iframe loading fails, try the streaming endpoint
              const streamUrl = `${dropboxBaseURL}/files/${file.id}/stream`;
              if (!previewUrl.includes('/stream')) {
                setPreviewUrl(streamUrl);
              } else {
                setError('Failed to load text file');
              }
            }}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <Modal show={true} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title 
          style={{ 
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            hyphens: 'auto',
            maxWidth: '90%'
          }}
          title={file.name}
        >
          <i className="fas fa-eye mr-2"></i>
          {file.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '80vh', overflow: 'auto' }}>
        {renderPreview()}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div className="text-muted">
            <small>
              Size: {(file.size_bytes / 1024 / 1024).toFixed(2)} MB
              {file.extension && ` • Type: .${file.extension}`}
            </small>
          </div>
          <div className="d-flex gap-2">
            {error && (
              <Button 
                variant="outline-primary" 
                onClick={async () => {
                  if (!canAccess?.download) {
                    toast.error('You do not have permission to download files');
                    return;
                  }
                  try {
                    const response = await dropboxService.downloadFile(file.id);
                    if (response?.success && response?.data?.downloadUrl) {
                      window.open(response.data.downloadUrl, '_blank');
                    } else {
                      toast.error('Failed to download file');
                    }
                  } catch (err) {
                    toast.error('Failed to download file');
                  }
                }}
                disabled={!canAccess?.download}
              >
                <i className="fas fa-download mr-2"></i>
                Download to View
              </Button>
            )}
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PreviewModal;

